import { ChangeDetectorRef, Component, HostListener, Injector, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Chip } from 'src/api/v3/common';
import { Company } from 'src/api/v3/company';
import { AuthService } from 'src/app/api/auth.service';
import { UserService } from 'src/app/api/user.service';
import { LanguageAware } from 'src/app/general/language-aware';
import { UResolvable } from 'src/app/guards/UResolvable';
import { FilterCriteria } from 'src/app/sadmin/company-list/company-list.component';
import { ItemConfiguration } from '../../components/company-columns/company-columns.component';
import { DataTableGetter } from '../../components/data-table/data-table.component';

@Component({
  selector: 'app-company-list-desktop',
  templateUrl: './company-list-desktop.component.html',
  styleUrls: ['./company-list-desktop.component.scss']
})
export class CompanyListDesktopComponent extends LanguageAware implements OnInit, UResolvable<typeof CompanyListDesktopComponent> {
  public canAdd = this.us.currentUser.permissions?.permissions.company_settings.create && this.us.currentUser.company_id === 0;
  public canDelete = this.us.currentUser.permissions?.permissions.company_settings.delete;
  public filterCriteria: FilterCriteria = { searchPhrase: '', searchFields: [], currentPage: 0, lastPage: 0 };
  filterChips: Chip[];
  public searchVisible = false;
  public isSearchLoading = false;
  private isManuallySubmitted = false;
  private typingTimer = null;
  private doneTypingInterval = 500;
  private resultsFiltered = false;
  public columns = this.persistenceService.bind('company_list_columns', ['logo', 'name', 'email', 'phone', 'address', this.canDelete ? 'fake:delete' : null]);
  public readonly columnConfig: ItemConfiguration<string>[] = [
    { name: this.trans('companies.labels.companyLogo'), value: 'logo' },
    { name: this.trans('companies.labels.companyName'), value: 'name' },
    { name: this.trans('companies.labels.companyEmail'), value: 'email' },
    { name: this.trans('companies.labels.companyPhone'), value: 'phone' },
    { name: this.trans('companies.labels.companyAddress'), value: 'address' },
    { name: this.trans('companies.labels.activeCompany'), value: 'active' },
    this.canDelete ? { name: this.trans('general.delete'), value: 'fake:delete' } : null,
  ];
  loader: DataTableGetter<Company>;

  constructor(cdRef: ChangeDetectorRef) {
    super(cdRef);
    this.filterChips = [
      { name: 'name', label: this.trans('companies.labels.companyName'), value: false, modifiableColumn: true },
      { name: 'email', label: this.trans('companies.labels.companyEmail'), value: false, modifiableColumn: true },
      { name: 'phone', label: this.trans('companies.labels.companyPhone'), value: false, modifiableColumn: true },
      { name: 'address', label: this.trans('companies.labels.companyAddress'), value: false, modifiableColumn: true },
      { name: 'active', label: this.trans('companies.labels.activeCompany'), value: false, modifiableColumn: false },
      { name: 'inactive', label: this.trans('companies.labels.inactiveCompany'), value: false, modifiableColumn: false },
    ];
    this.loader = this.companyService.getCompaniesGetter(null);
  }

  ngOnInit(): void {
  }

  private resetFilterCriteria(): void {
    this.filterCriteria = {
      searchPhrase: '',
      searchFields: [],
      currentPage: 0,
      lastPage: 0
    };
    this.filterChips.map(chip => chip.value = false);
  }

  public hideSearch(): void {
    if(!this.searchVisible) { return; }
    this.searchVisible = false;
    this.isSearchLoading = false;
    this.resetFilterCriteria();
    if(this.resultsFiltered) {
      this.loader = this.companyService.getCompaniesGetter(null);
      this.resultsFiltered = false;
    };
    const searchInputField = document.getElementById('searchInputField');
    searchInputField.blur();
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.hideSearch();
  }

  public showSearch(): void {
    this.searchVisible = true;
  }

  public onChipPress(event: any, chip: Chip): void {
    if(this.filterCriteria.searchFields.includes(chip.name) && !chip.value) {
      const index = this.filterCriteria.searchFields.indexOf(chip.name);
      this.filterCriteria.searchFields.splice(index, 1);
    } else if (!this.filterCriteria.searchFields.includes(chip.name) && chip.value) {
      this.filterCriteria.searchFields.push(chip.name);
    }
    this.onSearchSubmit(event);
  };

  public onSearchSubmit(event: any): void {
    clearTimeout(this.typingTimer);
    if(event.key === 'Enter' || event.type === 'click') {
      this.isManuallySubmitted = true;
      this.initializeSearch();
    } else {
      this.isManuallySubmitted = false;
      this.typingTimer = setTimeout(() => {
        this.initializeSearch();
      }, this.doneTypingInterval);
    }
  }

  private async initializeSearch(): Promise<void> {
    this.resetPagesCount();
    if(this.filterCriteria.searchPhrase.length >= (this.isManuallySubmitted ? 2 : 3)) {
      this.isSearchLoading = true;
      await this.setFilteredCompanies().then(() => {
        this.resultsFiltered = true;
        this.isSearchLoading = false;
      });
    } else if ( this.filterCriteria.searchPhrase.length === 0 && (this.filterCriteria.searchFields.includes('active') || this.filterCriteria.searchFields.includes('inactive')) ) {
      await this.setFilteredCompanies().then(() => {
        this.resultsFiltered = true;
        this.isSearchLoading = false;
      });
    } else if (this.resultsFiltered && this.filterCriteria.searchPhrase.length === 0) {
      this.loader = this.companyService.getCompaniesGetter(null);
      this.resultsFiltered = false;
    }
  }

  private setFilteredCompanies(): Promise<void> {
    this.filterActiveSearchFields();
    return new Promise((resolve) => {
      this.loader = this.companyService.getCompaniesGetter(this.filterCriteria);
      resolve();
    });
  }

  private filterActiveSearchFields(): void {
    this.filterCriteria.searchFields.map(searchFieldName => {
      if (!this.columns.value.includes(searchFieldName) && searchFieldName !== 'active' && searchFieldName !== 'inactive') {
        const index = this.filterCriteria.searchFields.indexOf(searchFieldName);
        this.filterCriteria.searchFields.splice(index, 1);
        const inactiveChip = this.filterChips.find(chip => chip.name === searchFieldName);
        inactiveChip.value = false;
      }
    });
  }

  private resetPagesCount(): void {
    this.filterCriteria.currentPage = -1;
    this.filterCriteria.lastPage = 0;
  }

  public static async resolve(injector: Injector, route: ActivatedRouteSnapshot): Promise<void> {
    const user = injector.get(UserService);
    if (!user.currentUser) {
      const auth = injector.get(AuthService);
      await auth.loadUserData();
    }
  }
}
